import { createTheme } from '@mui/material/styles';

// Couleurs BIAT - Palette Modern Forest (inspirée du logo)
const BIAT_COLORS = {
  primary: '#065F46',      // Vert forêt (du logo)
  secondary: '#047857',    // Vert émeraude
  accent: '#DC9B51',       // Or doux
  background: '#F5F7FA',   // Gris bleuté moderne
  backgroundAlt: '#FAFBFC',// Blanc cassé
  paper: '#ffffff',        // Blanc pur
  dark: '#064E3B',         // Vert très foncé
  light: '#D1FAE5',        // Vert pastel
  success: '#10B981',      // Vert succès
  warning: '#F59E0B',      // Orange warning
  error: '#EF4444',        // Rouge erreur
  info: '#3B82F6',         // Bleu info
  text: {
    primary: '#1F2937',    // Gris foncé pour texte principal
    secondary: '#6B7280'   // Gris moyen pour texte secondaire
  }
};

export const theme = createTheme({
  palette: {
    primary: {
      main: BIAT_COLORS.primary,
      light: BIAT_COLORS.secondary,
      dark: BIAT_COLORS.dark,
      contrastText: '#ffffff'
    },
    secondary: {
      main: BIAT_COLORS.secondary,
      light: BIAT_COLORS.light,
      dark: BIAT_COLORS.primary,
      contrastText: '#ffffff'
    },
    success: {
      main: BIAT_COLORS.success,
      contrastText: '#ffffff'
    },
    warning: {
      main: BIAT_COLORS.warning,
      contrastText: '#ffffff'
    },
    error: {
      main: BIAT_COLORS.error,
      contrastText: '#ffffff'
    },
    background: {
      default: BIAT_COLORS.background,
      paper: BIAT_COLORS.paper
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.02em',
      color: BIAT_COLORS.text.primary
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '-0.01em',
      color: BIAT_COLORS.text.primary
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '-0.01em',
      color: BIAT_COLORS.text.primary
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
      color: BIAT_COLORS.primary
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: BIAT_COLORS.primary
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      color: BIAT_COLORS.primary
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: BIAT_COLORS.text.primary
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: BIAT_COLORS.text.secondary
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em'
    }
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: BIAT_COLORS.primary,
          color: '#ffffff',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(10px)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          padding: '10px 24px',
          transition: 'all 0.2s ease-in-out'
        },
        contained: {
          backgroundColor: BIAT_COLORS.primary,
          boxShadow: '0 2px 8px rgba(6, 95, 70, 0.15)',
          '&:hover': {
            backgroundColor: BIAT_COLORS.dark,
            boxShadow: '0 4px 16px rgba(6, 95, 70, 0.3)',
            transform: 'translateY(-1px)'
          },
          '&:active': {
            transform: 'translateY(0)'
          }
        },
        outlined: {
          borderWidth: 2,
          borderColor: BIAT_COLORS.primary,
          color: BIAT_COLORS.primary,
          '&:hover': {
            borderWidth: 2,
            borderColor: BIAT_COLORS.dark,
            backgroundColor: BIAT_COLORS.light
          }
        },
        text: {
          color: BIAT_COLORS.primary,
          '&:hover': {
            backgroundColor: 'rgba(6, 95, 70, 0.05)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: BIAT_COLORS.paper
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: BIAT_COLORS.light,
            borderLeft: `4px solid ${BIAT_COLORS.primary}`,
            '&:hover': {
              backgroundColor: `${BIAT_COLORS.light}dd`
            }
          },
          '&:hover': {
            backgroundColor: `${BIAT_COLORS.light}88`
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06)',
            transform: 'translateY(-2px)'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.8125rem',
          height: 28
        },
        filled: {
          backgroundColor: BIAT_COLORS.light,
          color: BIAT_COLORS.dark
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none'
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)'
        },
        elevation2: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)'
        },
        elevation3: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.08)'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: BIAT_COLORS.primary,
              borderWidth: 2
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: BIAT_COLORS.primary,
              borderWidth: 2
            }
          }
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.05em',
            color: BIAT_COLORS.text.secondary,
            backgroundColor: BIAT_COLORS.backgroundAlt
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(6, 95, 70, 0.02)'
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          backgroundColor: BIAT_COLORS.primary
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500
        },
        standardSuccess: {
          backgroundColor: '#ECFDF5',
          color: '#065F46',
          '& .MuiAlert-icon': {
            color: '#10B981'
          }
        },
        standardError: {
          backgroundColor: '#FEF2F2',
          color: '#991B1B',
          '& .MuiAlert-icon': {
            color: '#EF4444'
          }
        },
        standardWarning: {
          backgroundColor: '#FFFBEB',
          color: '#92400E',
          '& .MuiAlert-icon': {
            color: '#F59E0B'
          }
        },
        standardInfo: {
          backgroundColor: '#EFF6FF',
          color: '#1E40AF',
          '& .MuiAlert-icon': {
            color: '#3B82F6'
          }
        }
      }
    }
  }
});



